import React from 'react';
import { useConfirm } from 'material-ui-confirm';
import { IconButton } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import Table, { columnType } from '../../../../components/ClearingTable/Table';
import Text from '../../../../components/Typography/Text';
import Button from '../../../../components/Buttons/Button';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
} from '../../../../components/Messages/Notification';
import QueryParam from '../../../../services/QueryParamService';
import {
  deletePendingOrderEntry,
  listPendingOrderEntry,
  listPendingBatchOrders,
  moveToTrades,
} from '../../../../services/OrderEntryService';
import OrderEntryMovePreviewModal from './OrderEntryMovePreviewModal';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderDateTime,
  CustomBodyRenderAlign,
  CustomBodyRenderQty,
} from '../../../../components/Table/CustomBodyRender';

export default function PendingTable(props) {
  const confirm = useConfirm();
  const { setIsViewPending } = props;
  const [loading, setLoading] = React.useState(false);
  const [selectedRowsIds, setSelectedRowsIds] = React.useState([]);
  const [tradeRequestsList] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [openMovePreview, setOpenMovePreview] = React.useState(false);
  const [selectedOrderEntriesData, setSelectedOrderEntriesData] = React.useState([]);

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              <div style={{ padding: 5 }}>
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDeletePending(rows[dataIndex])}
                >
                  <DeleteIcon style={{ height: 24, width: 24 }} />
                </IconButton>
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'orderEntryId',
      label: 'Row',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'trnsCount',
      label: 'TRNS Count',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'amt',
      label: 'Amt',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sell',
      label: 'Sell',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
    },
    {
      name: 'buy',
      label: 'Buy',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'note',
      label: 'Note',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'batchNo',
      label: 'Batch No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'modifiedDate',
      label: 'Modified Date',
      options: {
        customBodyRender: CustomBodyRenderDateTime,
      },
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    responsive: 'standard',
    onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
      setSelectedRowsIds(rowsSelected);
    },
    rowsSelected: selectedRowsIds,
    customToolbarSelect: (selectedRows) => {
      return (
        <div className="grd-row nm" style={{ margin: 0, padding: 0, marginRight: 10 }}>
          <div className="grd-cell">
            <Button
              disabled={loading}
              loading={loading}
              color="secondary"
              label={loading ? 'DELETING...' : 'BATCH DELETE '}
              onClick={() => handleDeletePending()}
            />
          </div>
        </div>
      );
    },
  };

  React.useEffect(() => {
    handleRefresh(true);
  }, []);

  const handleViewOrders = async (hideAlert) => {
    QueryParam.set({ isViewPending: false });
    setIsViewPending(false);
  };

  const handleRefresh = async (hideAlert) => {
    try {
      setSelectedRowsIds([]);
      setLoading(true);

      const data = await listPendingOrderEntry();
      setRows(data.orderEntriesList);
      if (!hideAlert) {
        notifyInfo(
          data.orderEntriesList.length > 0
            ? data.orderEntriesList.length + ' search result(s)'
            : 'No pending records found.'
        );
      }
    } catch (error) {
      setLoading(false);
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePending = async (data) => {
    let count = selectedRowsIds.length || 1;

    confirm({
      title: 'Delete',
      description: `Are you sure you want to delete ${count} pending items(s)?`,
      confirmationText: 'Yes, Delete',
      cancellationText: 'No, Cancel',
    })
      .then(async () => {
        try {
          setLoading(true);

          if (data) {
            await deletePendingOrderEntry(data.batchNo);
            notifySuccess('Pending order entry has been deleted.');
          } else {
            for (let index = 0; index < selectedRowsIds.length; index++) {
              const dataIndex = selectedRowsIds[index];
              await deletePendingOrderEntry(rows[dataIndex].batchNo);
            }

            notifySuccess(
              `${selectedRowsIds.length} pending order entry item(s) was deleted.`
            );
          }

          handleRefresh(true);
        } catch (err) {
          notifyError(err.message);
        } finally {
          setLoading(false);
        }
      })
      .catch(() => {
        console.error('error');
      });
  };

  const handleMoveToTrades = async () => {
    let batchOrders = {
      accountNos: [],
      batchNos: [],
    };

    for (let index = 0; index < selectedRowsIds.length; index++) {
      batchOrders.accountNos.push(rows[selectedRowsIds[index]].accountNo);
      batchOrders.batchNos.push(rows[selectedRowsIds[index]].batchNo);
    }

    const data = await listPendingBatchOrders({
      accountNo: batchOrders.accountNos,
      batchNo: batchOrders.batchNos,
    });

    setSelectedOrderEntriesData(data.orderEntriesList);
    setOpenMovePreview(true);
  };

  const handleMoveToTradesClose = async () => {
    try {
      setLoading(true);

      tradeRequestsList.splice(0);
      const tradeParam = {
        batchNos: [],
        accountNos: [],
      };

      for (let index = 0; index < selectedRowsIds.length; index++) {
        tradeParam.batchNos.push(rows[selectedRowsIds[index]].batchNo);
        tradeParam.accountNos.push(rows[selectedRowsIds[index]].accountNo);
        tradeRequestsList.push(rows[selectedRowsIds[index]]);
      }

      await moveToTrades({
        batchNo: tradeParam.batchNos,
        accountNo: tradeParam.accountNos,
        tradeRequests: tradeRequestsList,
      });

      setSelectedRowsIds([]);
      notifySuccess(`${selectedRowsIds.length} order entry item(s) was moved to trades.`);
      setOpenMovePreview(false);
      handleRefresh(true);
    } catch (err) {
      notifyError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Pending Orders" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              label={loading ? 'Searching...' : 'Refresh'}
              onClick={() => handleRefresh(false)}
            />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              label={loading ? 'Searching...' : 'View Trades'}
              onClick={handleViewOrders}
            />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={selectedRowsIds.length ? loading.save : true}
              label="Delete"
              onClick={() => handleDeletePending()}
            />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={selectedRowsIds.length ? loading.save : true}
              label="Move to Trades"
              onClick={() => handleMoveToTrades()}
            />
          </div>
        </div>

        <div className="mt-20">
          <Table
            title={'Pending Orders'}
            data={rows}
            columns={columns}
            options={options}
          />
        </div>

        {openMovePreview && (
          <OrderEntryMovePreviewModal
            orderEntries={selectedOrderEntriesData}
            count={selectedRowsIds.length}
            open={openMovePreview}
            close={() => {
              setOpenMovePreview(false);
            }}
            loading={loading}
            handleClose={handleMoveToTradesClose}
            isPending
          ></OrderEntryMovePreviewModal>
        )}
      </div>
    </React.Fragment>
  );
}
